import { FC, useContext, useEffect, useState } from 'react';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { GetPageProps, UserRegistrationState } from 'hooks/u002/useUserRegistrationHooks';
import { Form } from 'templates/Form';
import { Button } from 'atoms/Button';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { TemporaryRegistrationSchemaType } from 'commons/auditionForm';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { SubmitHandler } from 'react-hook-form';
import { TransitionButton } from 'atoms/TransitionButton';
import { useCreateTemporaryUser } from 'hooks/u002/useCreateTemporaryUserHooks';
import { AppContext } from 'templates/AppContextProvider';
import { AuthenticationResubmission } from './AuthenticationResubmission';
import { SelectCountryNumber } from 'atoms/SelectCountryNumber';
import { Country, getCountryCallingCode } from 'react-phone-number-input';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { useSearchParams } from 'react-router-dom';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useValidateStatus } from 'hooks/u002/useValidateStatus';
import { PopupErrorDialog } from 'organisms/u002/PopupErrorDialog';
import ReCaptchaErrorModal from 'organisms/ReCaptchaErrorModal';

//U002F05 仮アカウント作成画面
type UserPreRegistrationProps = GetPageProps<UserRegistrationState, 'TemporaryRegistration'>;
export const TemporaryRegistration: FC<UserPreRegistrationProps> = (props) => {
  const handleBack = () => props.onBack();
  const [searchParam] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);
  const authStatus = searchParam.get('status');
  const sessionId = searchParam.get('sessionId');
  const [modalMessage, setModalMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { currentLang, isGlobal } = useContext(AppContext);
  const [countryCode, setCountryCode] = useState(isGlobal ? 'US' : 'JP');
  const [popupErrorDialog, setPopupErrorDialog] = useState('');
  const [createTemporaryUser] = useCreateTemporaryUser();
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const [, validateLoading, validateStatusErr] = useValidateStatus({
    sessionId: sessionId ?? undefined,
    status: authStatus ?? undefined,
  });
  useEffect(() => {
    if (validateStatusErr) {
      setPopupErrorDialog(tAuditionForm('TU002_079'));
    }
  }, [validateStatusErr, tAuditionForm]);

  const [isLoading, setIsLoading] = useState(false);
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = props;
  const closeModal = () => {
    setShowModal(false);
  };
  const onSubmit: SubmitHandler<TemporaryRegistrationSchemaType> = async (form) => {
    console.log('form', form);
    setIsLoading(true);

    if (!executeRecaptcha) {
      setModalMessage(tAuditionForm('TU002_108'));
      setShowModal(true);
      return;
    }
    const recaptchaToken = await executeRecaptcha('submit_temporary_registration');

    const baseRequest = {
      phoneNumber: form.phoneNumber,
      path: window.location.origin + window.location.pathname,
      term_of_service_consent: form.term_of_service_consent,
      privacy_notice_consent: form.privacy_notice_consent,
      specific_use_consent: form.specific_use_consent,
      birth: form.birthday ?? null,
      lang: currentLang,
      recaptcha_token: recaptchaToken,
    };

    const request =
      authStatus === 'approved' && sessionId
        ? { ...baseRequest, status: authStatus, session_id: sessionId }
        : baseRequest;
    console.log('request', request);

    await createTemporaryUser({
      request,
      onSuccess: () => {
        setIsLoading(false);
        setIsSubmitted(true);
      },
      onError: (error) => {
        console.log({ error });
        if (error instanceof Error) {
          if (error.message === 'reCAPTCHA verification failed') {
            setShowRecaptchaModal(true);
            setIsLoading(false);
            return;
          }
          setModalMessage(
            error.message === 'MM002F0093E'
              ? tAuditionForm('TU002_049')
              : error.message === 'MU002F0128E'
                ? tAuditionForm('TU002_112')
                : tAuditionForm('TU002_050')
          );
          setShowModal(true);
          setIsLoading(false);
        }
      },
    });
  };

  if (isSubmitted) {
    return (
      <AuthenticationResubmission
        setIsSubmitted={setIsSubmitted}
        onClickSubmit={() => handleSubmit(onSubmit)()}
        phoneNumber={watch('phoneNumber')}
      />
    );
  }

  return (
    <>
      {(isLoading || validateLoading) && <LoadingModal />}
      {showModal && (
        <MessageModal
          message={modalMessage}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={closeModal}
        />
      )}
      {showRecaptchaModal && <ReCaptchaErrorModal onClose={() => setShowRecaptchaModal(false)} />}
      {popupErrorDialog && <PopupErrorDialog message={popupErrorDialog} />}
      <AppLogoHeader />
      <h1 className="sr-only" aria-live="assertive">
        {tAuditionForm('TU002_009')}
      </h1>
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tAuditionForm('TU002_009')}>
            <div className="flex flex-col items-center justify-center w-full">
              {/* 日本の電話番号表示（国識別番号表示なし） */}
              {!isGlobal && (
                <>
                  <div className="w-full mx-auto max-w-[18rem] mt-4">
                    <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                  </div>
                  <div className="flex items-center justify-start w-full max-w-[18rem] mx-auto mt-2">
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className="flex-grow border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] px-2 border-[1px]"
                      placeholder="08012345678"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const countryCallingCode = getCountryCallingCode(countryCode as Country);
                        const formattedValue = inputValue
                          ? `+${countryCallingCode}${inputValue}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }}
                    />
                  </div>
                </>
              )}
              {/* 海外の電話番号表示（国識別番号表示あり） */}
              {isGlobal && (
                <>
                  <div className="w-full max-w-md mx-auto mt-4">
                    <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                  </div>
                  <div className="flex items-center justify-start w-full max-w-md mx-auto mt-2">
                    <SelectCountryNumber
                      countryCode={countryCode}
                      setCountryCode={(code) => {
                        setCountryCode(code);
                        // 国選択後に既存の入力値があれば、国番号を更新
                        const phoneNumberValue = document.getElementById(
                          'phoneNumber'
                        ) as HTMLInputElement;
                        if (phoneNumberValue && phoneNumberValue.value) {
                          const countryCallingCode = getCountryCallingCode(code as Country);
                          const formattedValue = phoneNumberValue.value
                            ? `+${countryCallingCode}${phoneNumberValue.value}`
                            : '';
                          setValue('phoneNumber', formattedValue, { shouldValidate: true });
                        }
                      }}
                      className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background] mr-2"
                    />
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className="flex-grow border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] ml-2 px-2 border-[1px]"
                      placeholder="08012345678"
                      onChange={(e) => {
                        const countryCallingCode = getCountryCallingCode(countryCode as Country);
                        const formattedValue = e.target.value
                          ? `+${countryCallingCode}${e.target.value}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }}
                    />
                  </div>
                </>
              )}
              <FieldErrorMessages messages={[errors.phoneNumber?.message]} />
            </div>

            <div className={'h-[20px]'} />
            <div className={'flex justify-center items-center'}>
              <TransitionButton
                className="w-[10rem] mr-6"
                label={tAuditionForm('TU002_030')}
                onClick={handleBack}
              />
              <Button className="w-[10rem]" label={tAuditionForm('TU002_058')} type="submit" />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
};
